/**
 * WHETHER THIS AUTHOR MAY PUBLISH VIDEOS, AND THE WAY TO ASK (v1.0.29) - or, by `kind: 'stream'`,
 * announce live streams (1.2.0), a switch of its own beside video's, asked for the same way.
 *
 * The app's one session holds `videoAccess` and `streamAccess` (`GET /v1/me`), and asking answers
 * with the session, which the session provider takes at once - so every screen reading it moves
 * together. Where no session is held - a screen mounted bare, the default build with nobody signed
 * in - it reads and asks through the cabinet's API instead.
 */
import { useCallback, useState } from 'react';
import { useOptionalAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { TransportFailure, useCabinetApi } from '../../features/cabinet/api';
import type { AuthorSession, VideoAccess } from '../../features/cabinet/api/generated';
import { useAction, useAsync } from '../../features/cabinet/state';

/** Which switch: video's, or live streams'. Both read `none`, `requested` or `granted`. */
export type AccessKind = 'video' | 'stream';

export interface VideoAccessState {
  /** Undefined until it is known. A server from before the switch sends none, which reads `none`. */
  readonly access: VideoAccess | undefined;
  /**
   * Where the author stands - applicant, approved or on hold - undefined until it is known. Only
   * an approved author may ask: an applicant is refused `AUTHOR_APPROVAL_REQUIRED`, an author on
   * hold `AUTHOR_DISABLED`, so the menu says which instead of offering the ask.
   */
  readonly authorStatus: AuthorSession['authorStatus'] | undefined;
  /** True once this visit has asked and been answered: the screen says staff will answer. */
  readonly justRequested: boolean;
  readonly request: () => Promise<void>;
  readonly requesting: boolean;
  readonly error: unknown;
  readonly clearError: () => void;
  /**
   * Read the session again: a refusal said the switch is off for this author
   * (`VIDEO_ACCESS_REQUIRED`, `STREAM_ACCESS_REQUIRED`) while the session still said it was on -
   * staff took it back - so the menu is drawn from the answer rather than from what was held.
   */
  readonly recheck: () => void;
}

/** The session provider hands a refusal back as a value; the screen words a thrown one. */
class RequestNotSent extends Error {
  override readonly name = 'RequestNotSent';
}

function accessIn(session: AuthorSession, kind: AccessKind): VideoAccess {
  return (kind === 'stream' ? session.streamAccess : session.videoAccess) ?? 'none';
}

export function useVideoAccess(kind: AccessKind = 'video'): VideoAccessState {
  const auth = useOptionalAuthSession();
  const api = useCabinetApi();
  // The default build opens the cabinet with nobody signed in: no session is held there either.
  const held = auth?.state.session ?? null;
  const bare = held === null;
  const own = useAsync(
    useCallback(
      (signal: AbortSignal) => (bare ? api.getAuthorSession(signal) : Promise.resolve(null)),
      [bare, api],
    ),
    bare ? `${kind}-access:api` : `${kind}-access:session`,
  );
  const [answered, setAnswered] = useState<VideoAccess | undefined>(undefined);

  const ask = useAction(async (): Promise<VideoAccess> => {
    if (auth === null || bare) {
      const session =
        kind === 'stream' ? await api.requestStreamAccess() : await api.requestVideoAccess();
      return accessIn(session, kind);
    }
    const result =
      kind === 'stream' ? await auth.requestStreamAccess() : await auth.requestVideoAccess();
    if (!result.ok) {
      throw result.failure.kind === 'unreachable'
        ? new TransportFailure('The request did not reach the platform.')
        : new RequestNotSent(`The request was refused (${result.failure.kind}).`);
    }
    return accessIn(result.value, kind);
  });

  const session = bare ? own.data : held;
  const access = session === undefined || session === null ? undefined : accessIn(session, kind);

  const request = useCallback(async () => {
    const next = await ask.run();
    if (next !== undefined) {
      setAnswered(next);
    }
  }, [ask]);

  const { refetch } = own;
  const retryNow = auth?.retryNow;
  const recheck = useCallback(() => {
    setAnswered(undefined);
    if (bare) {
      refetch();
    } else {
      retryNow?.();
    }
  }, [bare, refetch, retryNow]);

  return {
    access: answered ?? access,
    authorStatus: session === undefined || session === null ? undefined : session.authorStatus,
    justRequested: answered === 'requested',
    request,
    requesting: ask.isRunning,
    error: ask.error,
    clearError: ask.clearError,
    recheck,
  };
}
